import React, { useCallback, useEffect, useState } from 'react';
import { Hourglass, Send, Clock, ChevronDown, ChevronRight, MailCheck, DoorOpen, PauseCircle } from 'lucide-react';
import { api, Button, Notice, formatWhen, timeAgo } from './ui';

/**
 * What the Stop agent is holding. A deal that stalls on terms is not closed:
 * the lead waits a few days, then the Stop agent checks in once more in the
 * same thread. Here you see every lead it is holding, why, what is still on
 * the table, and exactly when it will write next, with the check-in itself
 * once it is written.
 */

const clip = (s, n = 220) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

// What happens next, in words: a check-in being written or waiting, or the time it comes.
const nextLine = (h) => {
  const d = h.draft;
  if (d?.status === 'pending_approval') return { tone: 'warn', text: 'Check-in written: waiting for your approval in Approvals' };
  if (d?.status === 'scheduled' || d?.status === 'sending') return { tone: 'info', text: `Check-in written: goes ${formatWhen(d.send_at)} (${timeAgo(d.send_at)})` };
  if (!h.hold_until) return { tone: 'info', text: 'No time set' };
  const due = new Date(h.hold_until).getTime() <= Date.now();
  if (h.next === 'let_go') {
    return { tone: 'muted', text: due ? 'No reply to the last check-in: closing it as lost now' : `If they stay quiet, closes as lost ${formatWhen(h.hold_until)} (${timeAgo(h.hold_until)})` };
  }
  return { tone: 'info', text: due ? `Check-in ${(h.hold_checkins || 0) + 1} of ${h.checkins_of}: being written now` : `Check-in ${(h.hold_checkins || 0) + 1} of ${h.checkins_of}: ${formatWhen(h.hold_until)} (${timeAgo(h.hold_until)})` };
};

const TONE = {
  info: 'text-indigo-700 dark:text-indigo-300',
  warn: 'text-amber-700 dark:text-amber-300',
  muted: 'text-gray-500 dark:text-slate-400'
};

const Row = ({ label, children }) => (children ? (
  <div className="flex gap-3 py-0.5 text-sm">
    <span className="w-32 flex-none text-gray-500 dark:text-slate-400">{label}</span>
    <span className="min-w-0 text-gray-800 dark:text-slate-200">{children}</span>
  </div>
) : null);

const HoldCard = ({ h, onOpen, onAction, busy }) => {
  const [showDraft, setShowDraft] = useState(false);
  const next = nextLine(h);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <button type="button" onClick={() => onOpen(h.id)} className="font-semibold text-gray-900 hover:underline dark:text-slate-100">{h.domain}</button>
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {h.contact_name ? `${h.contact_name} · ` : ''}{h.contact_email || 'no address'}{h.recommended_geo ? ` · ${h.recommended_geo}` : ''}
            {h.held_at ? ` · on hold since ${formatWhen(h.held_at)}` : ''}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {!h.draft && h.next === 'checkin' && (
            <Button icon={Send} busy={busy === `checkin_now-${h.id}`} onClick={() => onAction(h.id, 'checkin_now')} title="The Stop agent writes the check-in on its next round">Check in now</Button>
          )}
          <Button icon={Clock} busy={busy === `hold_longer-${h.id}`} onClick={() => onAction(h.id, 'hold_longer')}>Give them longer</Button>
        </div>
      </div>

      <div className={`mt-3 flex items-center gap-1.5 text-sm font-medium ${TONE[next.tone]}`}>
        <Hourglass className="h-4 w-4 flex-none" />{next.text}
      </div>

      <div className="mt-2">
        <Row label="Why it stopped">{h.hold_reason}</Row>
        <Row label="On the table">{h.offer_line}</Row>
        <Row label="Their last words">{h.last_inbound?.body_text ? `“${clip(h.last_inbound.body_text)}” (${timeAgo(h.last_inbound.received_at)})` : null}</Row>
        <Row label="Check-ins written">{`${h.hold_checkins || 0} of ${h.checkins_of}`}</Row>
        {h.last_error && <Row label="Last problem"><span className="text-red-600 dark:text-red-400">{h.last_error}</span></Row>}
      </div>

      {h.draft?.body_text && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowDraft(!showDraft)} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300">
            {showDraft ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}The check-in it wrote
          </button>
          {showDraft && (
            <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-sans text-sm text-gray-800 dark:bg-slate-950 dark:text-slate-200">{h.draft.body_text}</pre>
          )}
          {showDraft && h.draft.approval_reason && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{h.draft.approval_reason}</p>}
        </div>
      )}
    </div>
  );
};

const RECENT = {
  checkin: { Icon: MailCheck, label: 'Check-in sent' },
  held: { Icon: PauseCircle, label: 'Put on hold' },
  let_go: { Icon: DoorOpen, label: 'Let go' }
};

export default function Holds({ teamId, onOpen, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api(`/teams/${teamId}/holds`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [teamId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onAction = async (id, action) => {
    setBusy(`${action}-${id}`);
    setError(null);
    try {
      await api(`/opportunities/${id}/action`, { method: 'POST', body: { action } });
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (!data) return error ? <Notice tone="error">{error}</Notice> : <div className="text-sm text-gray-500 dark:text-slate-400">Loading...</div>;

  const { holds = [], recent = [], settings = {} } = data;
  const off = settings.days === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-900 dark:bg-indigo-950/30">
        <Hourglass className="mt-0.5 h-5 w-5 flex-none text-indigo-600 dark:text-indigo-300" />
        <div className="text-sm text-indigo-900 dark:text-indigo-100">
          <div className="font-semibold">The Stop agent</div>
          {off ? (
            <p className="mt-0.5">Off for this team: a no on terms closes the deal straight away. Turn it on in Setup.</p>
          ) : (
            <p className="mt-0.5">
              When an affiliate will not meet us on terms, it does not close the deal. It steps back for {settings.days} day{settings.days === 1 ? '' : 's'}, then
              writes once more in the same thread, the way a good affiliate manager would: no pressure and never a better offer.
              It checks in {settings.checkins === 1 ? 'once' : `up to ${settings.checkins} times`}; a reply picks the negotiation up again, and silence after the last one closes it as lost.
            </p>
          )}
        </div>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      {holds.length ? (
        <div className="space-y-3">
          {holds.map((h) => <HoldCard key={h.id} h={h} onOpen={onOpen} onAction={onAction} busy={busy} />)}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500 dark:border-slate-800 dark:text-slate-400">
          Nothing on hold. When a deal stalls on terms, it shows up here with the reason and the time of its check-in.
        </div>
      )}

      {recent.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-slate-100">Lately</div>
          <ul className="space-y-1.5">
            {recent.map((r, i) => {
              const k = RECENT[r.kind] || RECENT.held;
              return (
                <li key={`${r.kind}-${r.opportunity_id}-${i}`} className="flex items-start gap-2 text-sm">
                  <k.Icon className="mt-0.5 h-4 w-4 flex-none text-gray-400 dark:text-slate-500" />
                  <span className="min-w-0 text-gray-700 dark:text-slate-300">
                    <span className="font-medium">{k.label}</span>
                    {r.domain && <> · <button type="button" className="hover:underline" onClick={() => onOpen(r.opportunity_id)}>{r.domain}</button></>}
                    <span className="text-gray-500 dark:text-slate-400"> · {timeAgo(r.at)}</span>
                    {r.detail && <span className="block text-xs text-gray-500 dark:text-slate-400">{clip(r.detail, 180)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
